<template>
  <div
    class="w-full ymap-container"
    :class="{
      fullscreen: isFullscreen,
      'is-loading-general-info': isGeneralInfoLoading,
    }"
  >
    <div class="ymap-toolbar">
      <div class="ymap-search-wrapper">
        <input
          v-model="cadastralNumber"
          class="ymap-search-input"
          type="text"
          @keyup.enter="getObjectToCadastralNumber"
          placeholder="Кадастр рақами бўйича қидириш"
        />
      </div>
      <button class="ymap-fullscreen-btn" @click="toggleFullscreen">
        {{ isFullscreen ? $t("exit_fullscreen") : $t("fullscreen") }}
      </button>
    </div>
    <div
      ref="leafletMap"
      class="leaflet-map"
      :style="{
        height: isFullscreen ? '100vh' : '600px',
        width: isFullscreen ? '100vw' : '100%',
      }"
    ></div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

export default {
  name: "dashboard-ymap",
  props: {
    finalconclusion: {
      type: [String, Number],
      default: "",
    },
    StatisticRegion: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      cadastralNumber: "",
      coords: [41.2, 64.5],
      buildingsList: [],
      regionsList: [],
      currentZoom: 6,
      mapKey: 0,
      mapInstance: null,
      baseTileLayer: null,
      regionMarkersLayer: null,
      regionBordersLayer: null,
      buildingMarkersLayer: null,
      generalInfoMarkersLayer: null,
      generalInfoGeometryLayer: null,
      generalInfoMarkerMap: {},
      showBuildings: false,
      selectedRegionId: null,
      isLoadingBuildings: false,
      loadingRegions: false,
      isGeneralInfoLoading: false,
      generalInfoPoints: [],
      isFullscreen: false,
      generalInfoRequestTimeout: null,
      generalInfoCancelSource: null,
      lastGeneralInfoBounds: null,
      buildingDetailsByGeneralInfoId: {},
      regions: {
        "UZ-AN": 1,
        "UZ-BU": 2,
        "UZ-JI": 3,
        "UZ-QA": 4,
        "UZ-NW": 5,
        "UZ-NG": 6,
        "UZ-SA": 7,
        "UZ-SU": 8,
        "UZ-SI": 9,
        "UZ-TK": 10,
        "UZ-TO": 11,
        "UZ-FA": 12,
        "UZ-XO": 13,
        "UZ-QR": 14,
      },
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale;
    },
    allBuildings() {
      const buildings = [];
      this.buildingsList.forEach((item) => {
        if (item.data && Array.isArray(item.data)) {
          item.data.forEach((building) => {
            // Проверяем что у здания есть координаты
            if (
              building.geolocation &&
              Array.isArray(building.geolocation) &&
              building.geolocation.length >= 2
            ) {
              buildings.push({
                ...building,
                parentColor: item.color,
                parentName: item.name_ru || item.name_uz,
                district: {
                  id: item.id,
                  name_cyr: item.name_cyr,
                  name_ru: item.name_ru,
                  name_uz: item.name_uz,
                },
              });
            }
          });
        }
      });
      return buildings;
    },
    visibleBuildings() {
      // Показываем только первые 500 зданий для производительности
      return this.allBuildings.slice(0, 500);
    },
    visibleGeneralInfoPoints() {
      // Ограничиваем количество отображаемых точек для производительности
      return this.generalInfoPoints.slice(0, 600);
    },
    showGeneralInfoData() {
      return this.currentZoom >= 11 && this.currentZoom < 15;
    },
  },
  watch: {
    currentZoom(newZoom) {
      console.log("currentZoom changed:", newZoom);
    },
    finalconclusion() {
      this.selectedRegionId = null;
      this.lastGeneralInfoBounds = null;
      this.loadGeoJsonBorders();
      this.fetchGeneralInformationByMapBounds();
    },
  },
  created() {
    console.log("🚀 Компонент ymap создан, начинаем инициализацию...");
  },
  mounted() {
    window.__ymapNavigate = (path) => this.$router.push(path);
    this.initLeafletMap();
    this.loadGeoJsonBorders();
    this.fetchGeneralInformationByMapBounds();
  },
  beforeDestroy() {
    if (this.generalInfoRequestTimeout) {
      clearTimeout(this.generalInfoRequestTimeout);
      this.generalInfoRequestTimeout = null;
    }
    this.setMapLoadingCursor(false);
    if (this.generalInfoCancelSource) {
      this.generalInfoCancelSource.cancel("Component destroyed");
      this.generalInfoCancelSource = null;
    }
    if (this.mapInstance) {
      this.mapInstance.off();
      this.mapInstance.remove();
      this.mapInstance = null;
    }
  },
  methods: {
    getObjectToCadastralNumber() {
      axios
        .get("/building/generalinformation/geo_map/", {
          params: {
            cadastral_number: this.cadastralNumber,
            coordinate: true,
            south: 37.144,
            north: 45.586,
            west: 55.998,
            east: 73.139,
          },
        })
        .then((response) => {
          const data = response.data;
          const item = Array.isArray(data)
            ? data[0]
            : data?.results?.[0] || data;
          const lat = Number(
            item?.coordinate?.latitude ??
              item?.coordinate?.lat ??
              item?.latitude,
          );
          const lon = Number(
            item?.coordinate?.longitude ??
              item?.coordinate?.lon ??
              item?.longitude,
          );

          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            this.coords = [lat, lon];
            this.currentZoom = Math.max(this.currentZoom, 15);
            this.$nextTick(async () => {
              this.zoomToObject([lat, lon]);
              await this.openCadastralSearchPopup(item, [lat, lon]);
            });
          } else {
            this.$notify({
              title: "Маълумот топилмади",
              message:
                "Кадастр рақами бўйича маълумот топилмади. Илтимос, кадастр рақамини текшириб қайта уриниг.",
              type: "warning",
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    },
    initLeafletMap() {
      if (this.mapInstance || !this.$refs.leafletMap) {
        return;
      }

      this.mapInstance = L.map(this.$refs.leafletMap, {
        zoomControl: true,
        preferCanvas: true,
      }).setView(this.coords, this.currentZoom);

      this.baseTileLayer = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution: "&copy; OpenStreetMap contributors",
          maxZoom: 19,
        },
      ).addTo(this.mapInstance);

      this.regionBordersLayer = L.layerGroup().addTo(this.mapInstance);
      this.regionMarkersLayer = L.markerClusterGroup({
        disableClusteringAtZoom: 14,
        spiderfyOnMaxZoom: false,
        zoomToBoundsOnClick: true,
        showCoverageOnHover: false,
      }).addTo(this.mapInstance);
      this.generalInfoGeometryLayer = L.layerGroup().addTo(this.mapInstance);
      this.generalInfoMarkersLayer = L.markerClusterGroup({
        disableClusteringAtZoom: 15,
        spiderfyOnMaxZoom: false,
        zoomToBoundsOnClick: true,
        showCoverageOnHover: false,
      }).addTo(this.mapInstance);
      this.buildingMarkersLayer = L.markerClusterGroup({
        disableClusteringAtZoom: 15,
        spiderfyOnMaxZoom: false,
        zoomToBoundsOnClick: true,
        showCoverageOnHover: false,
      }).addTo(this.mapInstance);

      this.mapInstance.on("click", this.clicked);
      this.mapInstance.on("zoomstart", this.onMapInteractionStart);
      this.mapInstance.on("moveend zoomend", this.onLeafletMoveEnd);
    },
    waitForMapReady() {
      return new Promise((resolve) => {
        const check = () => {
          if (this.mapInstance) {
            resolve(this.mapInstance);
          } else {
            setTimeout(check, 300);
          }
        };
        check();
      });
    },
    onMapInitialized() {
      this.fetchGeneralInformationByMapBounds();
    },
    onMapInteractionStart() {
      if (this.mapInstance) {
        this.mapInstance.closePopup();
      }
    },
    onLeafletMoveEnd() {
      if (!this.mapInstance) return;

      const center = this.mapInstance.getCenter();
      const bounds = this.mapInstance.getBounds();
      const newZoom = this.mapInstance.getZoom();

      this.currentZoom = newZoom;
      this.coords = [center.lat, center.lng];
      this.showBuildings = newZoom >= 10;

      this.scheduleGeneralInformationFetch([
        [bounds.getSouth(), bounds.getWest()],
        [bounds.getNorth(), bounds.getEast()],
      ]);
      this.renderDynamicLayers();
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      setTimeout(() => {
        this.reRender();
      }, 300);
    },
    reRender() {
      if (this.mapInstance) {
        this.mapInstance.invalidateSize();
      }
    },
    scheduleGeneralInformationFetch(bounds) {
      if (this.generalInfoRequestTimeout) {
        clearTimeout(this.generalInfoRequestTimeout);
      }

      this.generalInfoRequestTimeout = setTimeout(() => {
        if (!this.hasSignificantBoundsChange(bounds)) {
          return;
        }
        this.fetchGeneralInformationByBounds(bounds);
      }, 600);
    },
    hasSignificantBoundsChange(bounds) {
      if (!Array.isArray(bounds) || bounds.length < 2) {
        return false;
      }

      if (!this.lastGeneralInfoBounds) {
        return true;
      }

      const [[lat1, lon1], [lat2, lon2]] = bounds;
      const [
        [prevLat1, prevLon1],
        [prevLat2, prevLon2],
      ] = this.lastGeneralInfoBounds;

      const latDelta = Math.max(
        Math.abs(lat1 - prevLat1),
        Math.abs(lat2 - prevLat2),
      );
      const lonDelta = Math.max(
        Math.abs(lon1 - prevLon1),
        Math.abs(lon2 - prevLon2),
      );

      return latDelta >= 0.01 || lonDelta >= 0.01;
    },
    async fetchGeneralInformationByMapBounds() {
      const mapInstance = await this.waitForMapReady();
      const boundsObj = mapInstance.getBounds();
      const bounds = [
        [boundsObj.getSouth(), boundsObj.getWest()],
        [boundsObj.getNorth(), boundsObj.getEast()],
      ];
      this.fetchGeneralInformationByBounds(bounds);
    },
    fetchGeneralInformationByBounds(bounds) {
      if (!Array.isArray(bounds) || bounds.length < 2) {
        return;
      }

      const [[lat1, lon1], [lat2, lon2]] = bounds;

      if (this.currentZoom < 11) {
        this.generalInfoPoints = [];
        this.clearGeneralInfoGeometryLayer();
        this.lastGeneralInfoBounds = null;
        this.isGeneralInfoLoading = false;
        this.setMapLoadingCursor(false);
        return;
      }

      this.lastGeneralInfoBounds = bounds;
      const south = Math.min(lat1, lat2);
      const north = Math.max(lat1, lat2);
      const west = Math.min(lon1, lon2);
      const east = Math.max(lon1, lon2);
      const shouldDrawGeometry = this.currentZoom >= 15;
      const finalconclusion = this.finalconclusion || undefined;

      if (this.generalInfoCancelSource) {
        this.generalInfoCancelSource.cancel("Newer bounds request started");
      }
      this.generalInfoCancelSource = axios.CancelToken.source();
      this.isGeneralInfoLoading = true;
      this.setMapLoadingCursor(true);

      axios
        .get("/building/generalinformation/geo_map/", {
          params: {
            south,
            north,
            west,
            east,
            coordinate: true,
            geometry: shouldDrawGeometry,
            finalconclusion,
          },
          cancelToken: this.generalInfoCancelSource.token,
        })
        .then((response) => {
          const rows = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.results)
            ? response.data.results
            : [];

          this.generalInfoPoints = rows
            .map((item) => {
              const lat = Number(item?.coordinate?.latitude);
              const lon = Number(item?.coordinate?.longitude);

              if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                return null;
              }

              return {
                id: item.id,
                name_ru: item.name_ru,
                name_uz: item.name_uz,
                name_cyr: item.name_cyr,
                finalconclusion: item.finalconclusion,
                cadastral_number: item.cadastral_number,
                region: item.region,
                district: item.district,
                geolocation: [lat, lon],
              };
            })
            .filter(Boolean);

          if (shouldDrawGeometry) {
            this.drawGeneralInfoGeometries(rows);
          } else {
            this.clearGeneralInfoGeometryLayer();
          }

          this.renderDynamicLayers();
        })
        .catch((error) => {
          if (axios.isCancel && axios.isCancel(error)) {
            return;
          }
          console.error("Error fetching general information:", error);
        })
        .finally(() => {
          this.isGeneralInfoLoading = false;
          this.setMapLoadingCursor(false);
        });
    },
    setMapLoadingCursor(isLoading) {
      const cursorValue = isLoading ? "wait" : "";
      document.body.style.cursor = cursorValue;

      if (this.$refs.leafletMap) {
        this.$refs.leafletMap.style.cursor = cursorValue;
      }

      if (
        this.mapInstance &&
        typeof this.mapInstance.getContainer === "function"
      ) {
        const container = this.mapInstance.getContainer();
        if (container) {
          container.style.cursor = cursorValue;
        }
      }
    },
    async drawGeneralInfoGeometries(rows) {
      const geometryItems = rows.filter(
        (item) => item?.geometry && item.geometry?.coordinates,
      );

      this.clearGeneralInfoGeometryLayer();
      if (!geometryItems.length) {
        return;
      }

      await this.waitForMapReady();

      geometryItems.forEach((item) => {
        const polygons = this.getGeometryPolygonsLatLon(item.geometry);

        const lat = Number(item?.coordinate?.latitude);
        const lon = Number(item?.coordinate?.longitude);
        const popupContent = this.getGeneralInfoBalloonContent({
          id: item.id,
          cadastral_number: item.cadastral_number,
          region: item.region,
          district: item.district,
          geolocation: [lat, lon],
        });

        polygons.forEach((polygonCoords) => {
          const polygon = L.polygon(polygonCoords, {
            fillColor: item.color, // blue-600
            color: item.color, // blue-600
            weight: 2,
            fillOpacity: 0.32, // slightly more visible
            opacity: 0.7,
          });
          polygon.bindTooltip(
            `${this.$t("cadaster_number")}: ${item.cadastral_number ||
              this.$t("not_specified")}`,
          );
          polygon.bindPopup(popupContent, this.getGeneralInfoPopupOptions());
          polygon.on("click", async () => {
            console.log("item", item);

            const details = await this.fetchBuildingDetailsByGeneralInfoId(
              item.id,
            );
            const contentWithDetails = this.getGeneralInfoBalloonContent(
              {
                id: item.id,
                cadastral_number: item.cadastral_number,
                region: item.region,
                district: item.district,
                geolocation: [lat, lon],
              },
              details,
            );
            polygon.setPopupContent(contentWithDetails);
            polygon.openPopup();
          });
          this.generalInfoGeometryLayer.addLayer(polygon);
        });
      });
    },
    clearGeneralInfoGeometryLayer() {
      if (this.generalInfoGeometryLayer) {
        this.generalInfoGeometryLayer.clearLayers();
      }
    },
    getGeometryPolygonsLatLon(geometry) {
      if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
        return [];
      }

      const crsName = geometry?.crs?.properties?.name || "";
      const isEpsg3857 = String(crsName)
        .toUpperCase()
        .includes("3857");

      const project = (coordPair) => {
        if (!Array.isArray(coordPair) || coordPair.length < 2) {
          return null;
        }

        const x = Number(coordPair[0]);
        const y = Number(coordPair[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) {
          return null;
        }

        if (isEpsg3857) {
          return this.fromEpsg3857ToLatLon(x, y);
        }

        // GeoJSON default tartibi [lon, lat], Yandex uchun [lat, lon]
        return [y, x];
      };

      const mapRing = (ring) => ring.map(project).filter(Boolean);

      if (geometry.type === "Polygon") {
        const polygon = geometry.coordinates
          .map(mapRing)
          .filter((ring) => ring.length >= 3);

        return polygon.length ? [polygon] : [];
      }

      if (geometry.type === "MultiPolygon") {
        return geometry.coordinates
          .map((polygon) =>
            polygon.map(mapRing).filter((ring) => ring.length >= 3),
          )
          .filter((polygon) => polygon.length);
      }

      return [];
    },
    fromEpsg3857ToLatLon(x, y) {
      const lon = (x / 20037508.34) * 180;
      let lat = (y / 20037508.34) * 180;
      lat =
        (180 / Math.PI) *
        (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2);
      return [lat, lon];
    },
    getGeneralInfoHintContent(point) {
      const cadaster = point.cadastral_number || this.$t("not_specified");
      return `${this.$t("cadaster_number")}: ${cadaster}`;
    },
    getGeneralInfoPopupOptions() {
      return {
        minWidth: 420,
        maxWidth: 780,
      };
    },
    getGeneralInfoBalloonContent(point, details = null) {
      const regionName =
        point.region?.[`name_${this.currentLang}`] ||
        point.region?.name_ru ||
        point.region?.name_uz ||
        "-";
      const districtName =
        point.district?.[`name_${this.currentLang}`] ||
        point.district?.name_ru ||
        point.district?.name_uz ||
        "-";
      const coordsText = Array.isArray(point.geolocation)
        ? `${point.geolocation[0].toFixed(5)}, ${point.geolocation[1].toFixed(
            5,
          )}`
        : "";
      const objectInfoHref = point?.id
        ? `/objects/object-info/${point.id}`
        : "#";
      const cadastralText = point.cadastral_number || "-";
      const detailsHtml = this.getBuildingDetailsHtml(details);

      return `
        <div style="padding: 6px 0; min-width: 380px; max-width: 740px; font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a;">
          <div style="background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px 12px; margin-bottom: 10px;">
            <div style="font-size: 12px; color: #1e40af; margin-bottom: 2px;">${this.$t(
              "object",
            )}</div>
            <div style="font-size: 16px; font-weight: 700; color: #1e3a8a; line-height: 1.2;">#${
              point.id
            }</div>
          </div>

          <div style="display: grid; grid-template-columns: 130px 1fr; gap: 6px 10px; align-items: start; font-size: 13px;">
            <div style="color: #64748b;">${this.$t("cadaster_number")}</div>
            <div style="font-weight: 600; word-break: break-word;">
              <a href="${objectInfoHref}" style="color: #1d4ed8; text-decoration: underline;" title="${this.$t(
        "object",
      )} #${point.id}">${cadastralText}</a>
            </div>

            <div style="color: #64748b;">${this.$t("region")}</div>
            <div style="font-weight: 500; word-break: break-word;">${regionName}</div>

            <div style="color: #64748b;">${this.$t("district_or_city")}</div>
            <div style="font-weight: 500; word-break: break-word;">${districtName}</div>
          </div>

          <div style="margin-top: 10px; display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; background: #f1f5f9; color: #334155; font-size: 12px; border: 1px solid #e2e8f0;">
            <span style="color: #64748b;">${this.$t("coor")}:</span>
            <span style="font-weight: 600;">${coordsText || "-"}</span>
          </div>

          ${detailsHtml}
        </div>
      `;
    },
    async fetchBuildingDetailsByGeneralInfoId(generalInfoId) {
      if (!generalInfoId) {
        return null;
      }

      if (this.buildingDetailsByGeneralInfoId[generalInfoId]) {
        return this.buildingDetailsByGeneralInfoId[generalInfoId];
      }

      try {
        const response = await axios.get(
          `/building/generalinformation/${generalInfoId}/buildings/`,
        );
        this.$set(
          this.buildingDetailsByGeneralInfoId,
          generalInfoId,
          response.data,
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching building details:", error);
        return null;
      }
    },
    getBuildingDetailsHtml(details) {
      if (!details) {
        return "";
      }

      const detailsList = Array.isArray(details)
        ? details
        : Array.isArray(details?.results)
        ? details.results
        : [details];

      if (!detailsList.length) {
        return "";
      }

      const rowsHtml = detailsList
        .map((item, index) => {
          const localizedName =
            item?.[`name_${this.currentLang}`] ||
            item?.name_ru ||
            item?.name_uz ||
            item?.name_cyr ||
            "-";
          const conclusionName =
            item?.finalconclusion?.[`name_${this.currentLang}`] ||
            item?.finalconclusion?.name_ru ||
            item?.finalconclusion?.name_uz ||
            item?.finalconclusion?.name_cyr ||
            "-";
          const conclusionId = Number(
            item?.finalconclusion?.id || item?.finalconclusion,
          );
          const buildingId = item?.id;
          const cadastralHref = buildingId
            ? `/exist-build/${buildingId}/0?id=${buildingId}`
            : "#";
          const cadastralText = item?.cadastral_number || "-";

          let tagTypeClass = "el-tag--info";
          if (conclusionId === 1) {
            tagTypeClass = "ymap-conclusion-badge--success";
          } else if (conclusionId === 2) {
            tagTypeClass = "ymap-conclusion-badge--warning";
          } else if (conclusionId === 3 || conclusionId === 4) {
            tagTypeClass = "ymap-conclusion-badge--danger";
          } else {
            tagTypeClass = "ymap-conclusion-badge--info";
          }

          return `
          <tr style="border-bottom: 1px solid #e5e7eb;">
            <td style="padding: 4px 6px; white-space: nowrap;">${index + 1}</td>
            <td style="padding: 4px 6px; white-space: nowrap;">
              <a href="${cadastralHref}" style="color: #1d4ed8; text-decoration: underline;">${cadastralText}</a>
            </td>
            <td style="padding: 4px 6px;">${localizedName}</td>
            <td style="padding: 4px 6px; text-align: center; white-space: nowrap;">${item?.year_construction ||
              "-"}</td>
            <td style="padding: 4px 6px; text-align: center; white-space: nowrap;">
              <span class="ymap-conclusion-badge ${
                item?.buildingtype === "NEW"
                  ? "ymap-conclusion-badge--success"
                  : "ymap-conclusion-badge--warning"
              }">
                ${
                  item?.buildingtype === "NEW"
                    ? this.$t("new_building")
                    : this.$t("existing_building")
                }
              </span>
            </td>
            <td style="padding: 4px 6px;">
              <span class="ymap-conclusion-badge ${tagTypeClass}">
                ${conclusionName}
              </span>
            </td>
          </tr>
        `;
        })
        .join("");

      return `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
          <div style="font-weight: 600; margin-bottom: 6px;">${this.$t(
            "building_list",
          )} (${detailsList.length})</div>
          <div style="max-height: 220px; overflow: auto; border: 1px solid #e5e7eb; border-radius: 6px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead style="position: sticky; top: 0; background: #f8fafc; z-index: 1;">
                <tr>
                  <th style="padding: 6px; text-align: left;">#</th>
                  <th style="padding: 6px; text-align: left;">${this.$t(
                    "cadaster_number",
                  )}</th>
                  <th style="padding: 6px; text-align: left;">${this.$t(
                    "name",
                  )}</th>
                  <th style="padding: 6px; text-align: center;">${this.$t(
                    "year_construction",
                  )}</th>
                  <th style="padding: 6px; text-align: center;">${this.$t(
                    "structure_type",
                  )}</th>
                  <th style="padding: 6px; text-align: left;">${this.$t(
                    "finalconclusion",
                  )}</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
          </div>
        </div>
      `;
      // <span :class="buildingtype === 'NEW' ? 'new_build' : 'exist_build'" class="px-2.5 py-1 rounded-lg "
      //         size="small" :type="buildingtype === 'NEW' ? 'success' : 'info'">{{
      //           scope.row.buildingtype === "NEW"
      //             ? $t("new_building")
      //             : $t("existing_building")
      //         }}
      //       </span>
    },
    createLeafletIconFromYmapConfig(config) {
      if (!config?.imageHref) {
        return undefined;
      }

      const iconSize = config.imageSize || [28, 28];
      const iconAnchor = config.imageOffset
        ? [Math.abs(config.imageOffset[0]), Math.abs(config.imageOffset[1])]
        : [iconSize[0] / 2, iconSize[1] / 2];

      return L.icon({
        iconUrl: config.imageHref,
        iconSize,
        iconAnchor,
      });
    },
    renderRegionMarkers() {
      if (!this.regionMarkersLayer) return;
      this.regionMarkersLayer.clearLayers();

      this.regionsList.forEach((region) => {
        if (!Array.isArray(region.geolocation) || region.geolocation.length < 2)
          return;
        const title = region.name_ru || region.name_uz;

        const popupContent =
          this.getRegionStatisticsPopupContent(region) ||
          title ||
          this.$t("region");
        const marker = L.marker(region.geolocation, {
          icon: this.createLeafletIconFromYmapConfig(
            this.getRegionIcon(region),
          ),
        });
        marker.bindTooltip(title || this.$t("region"));
        marker.bindPopup(popupContent, this.getGeneralInfoPopupOptions());
        marker.on("click", (event) => {
          if (this.currentZoom < 15) {
            event.target.openPopup();
            return;
          }
          this.regionClicked(region);
        });
        this.regionMarkersLayer.addLayer(marker);
      });
    },
    getRegionStatisticsPopupContent(region) {
      const stat = this.StatisticRegion.find(
        (item) => item?.id === this.regions[region.id],
      );

      if (!stat) {
        return null;
      }
      console.log(region, stat);

      const regionTitle =
        stat.region ||
        stat.region_ru ||
        region.name_ru ||
        region.name_uz ||
        this.$t("region");
      return `
        <div style="padding: 10px 12px; font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; min-width: 300px; max-width: 420px;">
          <div style="font-size: 16px; font-weight: 700; margin-bottom: 8px;">${regionTitle}</div>
          <div style="display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; font-size: 13px;">
            <!-- Mavjud binolar - To'q kulrang/ko'kish (Neytral) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/building-list/exg?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #f1f5f9; display: flex; justify-content: space-between; border: 1px solid #cbd5e1; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "existing_building",
              )}</div>
              <div>${stat.count_existing_buildings?.toLocaleString() ??
                "-"}</div>
            </a>
            <!-- Yangi binolar - Och binafsha/ko'k (Yangi tushunchasi uchun) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/building-list/new?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #f5f3ff; display: flex; justify-content: space-between; border: 1px solid #ddd6fe; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "new_building",
              )}</div>
              <div>${stat.count_new_buildings?.toLocaleString() ?? "-"}</div>
            </a>
            <!-- Yaroqli binolar - Och yashil (Ijobiy) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/statistica/building/1,2?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #ecfdf5; display: flex; justify-content: space-between; border: 1px solid #bbf7d0; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "valid_buildings",
              )}</div>
              <div>${stat.count_sustainable?.toLocaleString() ?? "-"}</div>
            </a>
            <!-- Yaroqsiz binolar - Och qizil (Salbiy) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/statistica/building/4?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #fee2e2; display: flex; justify-content: space-between; border: 1px solid #fecaca; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "invalid_buildings",
              )}</div>
              <div>${stat.count_not_sustainable4?.toLocaleString() ?? "-"}</div>
            </a>
            <!-- Barqaror bo'lmagan binolar - Och sariq/olovrang (Ogohlantirish) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/statistica/building/3?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #fef3c7; display: flex; justify-content: space-between; border: 1px solid #fde68a; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "not_sustainable_buildings",
              )}</div>
              <div>${stat.count_not_sustainable?.toLocaleString() ?? "-"}</div>
            </a>
            <!-- Tasdiqlanmagan binolar - Och ko'k (Ma'lumot) -->
            <a href="#" onclick="event.preventDefault(); window.__ymapNavigate('/building-list/newcadaster?page=1&region=${stat.shared_id ||
              ""}')" style="grid-column: span 3; background: #eff6ff; display: flex; justify-content: space-between; border: 1px solid #bfdbfe; border-radius: 10px; padding: 10px; text-decoration: none; color: inherit; cursor: pointer;">
              <div style="font-weight: 600; min-height: 34px">${this.$t(
                "unattested_count",
              )}</div>
              <div>${stat.count_building?.toLocaleString() ?? "-"}</div>
            </a>
          </div>
        </div>
      `;
    },
    renderGeneralInfoMarkers() {
      if (!this.generalInfoMarkersLayer) return;

      if (!this.showGeneralInfoData) {
        this.generalInfoMarkersLayer.clearLayers();
        this.generalInfoMarkerMap = {};
        return;
      }

      const visibleIds = new Set(
        this.visibleGeneralInfoPoints.map((p) => p.id),
      );

      // Ortiqcha markerlarni o'chirish
      Object.keys(this.generalInfoMarkerMap).forEach((id) => {
        if (!visibleIds.has(Number(id))) {
          this.generalInfoMarkersLayer.removeLayer(
            this.generalInfoMarkerMap[id],
          );
          delete this.generalInfoMarkerMap[id];
        }
      });

      // Faqat yangi pointlar uchun marker qo'shish
      this.visibleGeneralInfoPoints.forEach((point) => {
        if (!Array.isArray(point.geolocation) || point.geolocation.length < 2)
          return;
        if (this.generalInfoMarkerMap[point.id]) return;

        const marker = L.marker(point.geolocation, {
          icon: this.createLeafletIconFromYmapConfig(
            this.getBuildingIcon(point),
          ),
        });
        marker.bindTooltip(this.getGeneralInfoHintContent(point));
        marker.bindPopup(
          this.getGeneralInfoBalloonContent(point),
          this.getGeneralInfoPopupOptions(),
        );
        marker.on("popupopen", async (event) => {
          if (!point?.id) {
            return;
          }

          const details = await this.fetchBuildingDetailsByGeneralInfoId(
            point.id,
          );
          if (!details) {
            return;
          }

          const targetMarker = event.target;
          if (
            targetMarker &&
            targetMarker.isPopupOpen &&
            targetMarker.isPopupOpen()
          ) {
            targetMarker.setPopupContent(
              this.getGeneralInfoBalloonContent(point, details),
            );
          }
        });
        this.generalInfoMarkersLayer.addLayer(marker);
        this.generalInfoMarkerMap[point.id] = marker;
      });
    },
    renderBuildingMarkers() {
      if (this.buildingMarkersLayer) {
        this.buildingMarkersLayer.clearLayers();
      }
    },
    zoomToObject(geolocation) {
      if (
        !this.mapInstance ||
        !Array.isArray(geolocation) ||
        geolocation.length < 2
      ) {
        return;
      }

      const targetZoom = Math.max(this.currentZoom, 17);
      this.mapInstance.setView(geolocation, targetZoom, {
        animate: true,
      });
    },
    renderDynamicLayers() {
      this.renderGeneralInfoMarkers();
      this.renderBuildingMarkers();
    },
    async openCadastralSearchPopup(item, geolocation) {
      if (
        !this.mapInstance ||
        !Array.isArray(geolocation) ||
        geolocation.length < 2
      ) {
        return;
      }

      const searchPoint = {
        id: item?.id,
        cadastral_number: item?.cadastral_number,
        region: item?.region,
        district: item?.district,
        geolocation,
      };

      const popup = L.popup({
        maxWidth: 780,
        minWidth: 420,
        autoClose: true,
        closeButton: true,
        closeOnClick: false,
      })
        .setLatLng(geolocation)
        .setContent(this.getGeneralInfoBalloonContent(searchPoint));

      popup.openOn(this.mapInstance);

      if (!item?.id) {
        return;
      }

      const details = await this.fetchBuildingDetailsByGeneralInfoId(item.id);
      if (!details) {
        return;
      }

      popup.setContent(this.getGeneralInfoBalloonContent(searchPoint, details));
    },
    async loadGeoJsonBorders() {
      await this.waitForMapReady();
      try {
        if (this.regionBordersLayer) {
          this.regionBordersLayer.clearLayers();
        }
        if (this.regionMarkersLayer) {
          this.regionMarkersLayer.clearLayers();
        }

        const response = await fetch("/Uzbekistan_ADM1.geojson");
        const geojson = await response.json();
        if (!geojson.features) return;

        geojson.features.forEach((feature) => {
          let coords = [];
          if (feature.geometry.type === "Polygon") {
            coords = feature.geometry.coordinates;
          } else if (feature.geometry.type === "MultiPolygon") {
            coords = feature.geometry.coordinates.flat();
          }
          // Яндекс требует [lat, lng], geojson обычно [lng, lat]
          const leafletCoords = coords.map((polygon) =>
            polygon.map(([lng, lat]) => [lat, lng]),
          );

          // Найти цвет региона по shapeName
          let fillColor = "#93c5fd";
          let strokeColor = "#2C4084";

          const polygon = L.polygon(leafletCoords, {
            fillColor,
            color: strokeColor,
            weight: 2,
            fillOpacity: 0.22,
          });
          polygon.bindTooltip(
            feature.properties?.shapeName || this.$t("region"),
          );
          polygon.on("click", () => {
            if (this.currentZoom > 10) {
              return;
            }
            const shapeName = feature.properties?.shapeName;
            const shapeISO = feature.properties?.shapeISO;
            const featureRegion = {
              id: shapeISO,
              region_ru: shapeName,
              region: shapeName,
              name_ru: shapeName,
              name_uz: shapeName,
              shapeName,
            };
            const popupContent = this.getRegionStatisticsPopupContent(
              featureRegion,
            );

            if (popupContent) {
              L.popup({
                maxWidth: 780,
                minWidth: 420,
                autoClose: true,
                closeButton: true,
                closeOnClick: false,
              })
                .setLatLng(polygon.getBounds().getCenter())
                .setContent(popupContent)
                .openOn(this.mapInstance);
              return;
            }

            L.popup({
              maxWidth: 420,
              minWidth: 280,
              autoClose: true,
              closeButton: true,
              closeOnClick: false,
            })
              .setLatLng(polygon.getBounds().getCenter())
              .setContent(
                `<div style="padding: 12px; font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a;">${shapeName ||
                  this.$t(
                    "region",
                  )}<br><small style="color:#64748b;">Ma'lumot topilmadi</small></div>`,
              )
              .openOn(this.mapInstance);
            return;
          });
          this.regionBordersLayer.addLayer(polygon);
        });

        this.renderDynamicLayers();
      } catch (e) {
        console.error("Ошибка загрузки или отображения geoJSON:", e);
      }
    },
    getLabelIcon(regionName) {
      const textWidth = regionName.length * 8 + 16;
      return {
        layout: "default#image",
        imageHref:
          "data:image/svg+xml;charset=utf-8," +
          encodeURIComponent(`
          <svg width="${textWidth}" height="34" viewBox="0 0 ${textWidth} 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="8" width="${textWidth -
              4}" height="18" rx="3" fill="white" stroke="#333" stroke-width="1"/>
            <text x="${textWidth /
              2}" y="19" text-anchor="middle" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#333">${regionName}</text>
          </svg>
        `),
        imageSize: [textWidth, 34],
        imageOffset: [-textWidth / 2, -17],
      };
    },
    fetchBuildings(regionId) {
      this.isLoadingBuildings = true;
      this.selectedRegionId = regionId;
      axios
        .get(
          `/building/specification-short/?region=${regionId}&include_data=true${
            this.finalconclusion
              ? `&finalconclusion=${this.finalconclusion}`
              : ""
          }`,
        )
        .then((response) => {
          this.buildingsList = response.data;
          this.isLoadingBuildings = false;

          // После загрузки данных просто включаем показ зданий
          this.$nextTick(() => {
            this.showBuildings = true;
            this.renderBuildingMarkers();
          });
        })
        .catch((error) => {
          console.error("Error fetching buildings:", error);
          this.isLoadingBuildings = false;
        });
    },
    fetchRegions() {
      this.loadingRegions = true;
      axios
        .get(
          `/building/specification-short/${
            this.finalconclusion
              ? `?finalconclusion=${this.finalconclusion}`
              : ""
          }`,
        )
        .then((response) => {
          this.regionsList = response.data;
          // После загрузки регионов рисуем полигоны
          this.loadGeoJsonBorders();
        })
        .catch((error) => {
          console.error("Error fetching regions:", error);
        })
        .finally(() => {
          this.loadingRegions = false;
        });
    },
    clicked(e) {
      if (e?.latlng) {
        this.coords = [e.latlng.lat, e.latlng.lng];
      }
    },
    regionClicked(region) {
      // При клике на регион просто зумируем к нему
      this.zoomToRegion(region);
      console.log("Clicked region:", region.name_ru || region.name_uz);
    },
    zoomToRegion(region) {
      if (!this.mapInstance) return;

      // Зумируем к региону с уровнем 11 (достаточно для загрузки зданий)
      this.mapInstance.setView(region.geolocation, 11, {
        animate: true,
      });
    },
    loadBuildingsForCurrentView() {
      return;
    },
    getCurrentRegion() {
      // Находим ближайший регион к текущему центру карты
      let closestRegion = null;
      let minDistance = Infinity;

      this.regionsList.forEach((region) => {
        if (!region.geolocation) return;

        const distance = this.calculateDistance(
          this.coords[0],
          this.coords[1],
          region.geolocation[0],
          region.geolocation[1],
        );

        if (distance < minDistance) {
          minDistance = distance;
          closestRegion = region;
        }
      });

      return closestRegion;
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      // Простое вычисление расстояния между двумя точками
      const R = 6371; // Радиус Земли в км
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c;
      return d;
    },
    deg2rad(deg) {
      return deg * (Math.PI / 180);
    },
    getConclusionText(conclusion) {
      // Multilanguage support
      const conclusions = {
        1: "I–сейсмик мустаҳкамлиги меъёрда",
        2: "II–сейсмик мустаҳкамлиги қониқарли",
        3: "III–сейсмик мустаҳкамлиги қониқарсиз, инструментал-техник текширувдан ўтказиш талаб этилади",
        4: "IV–мустаҳкамлаш талаб этилади, авария ҳолатига мойил, авария ҳолатида",
      };
      return conclusions[conclusion] || "-";
    },
    getConclusionColor(conclusion) {
      const colors = {
        1: "#155724",
        2: "#856404",
        3: "#721c24",
        4: "#721c24",
      };
      return colors[conclusion] || "#3B7597";
    },
    getConclusionBgColor(conclusion) {
      const colors = {
        1: "#d4edda",
        2: "#fff3cd",
        3: "#f8d7da",
        4: "#f5c6cb",
      };
      return colors[conclusion] || "#e9ecef";
    },
    fitBoundsToBuildings() {
      console.log(
        "fitBoundsToBuildings called, buildingsList length:",
        this.buildingsList.length,
      );
      if (!this.$refs.yandexMap || !this.buildingsList.length) return;

      // Проверяем что карта готова
      const mapInstance =
        this.$refs.yandexMap.myMap || this.$refs.yandexMap.$ymapInstance;
      if (!mapInstance) {
        // Если карта еще не готова, попробуем через небольшую задержку
        setTimeout(() => {
          this.fitBoundsToBuildings();
        }, 500);
        return;
      }

      // Собираем все координаты зданий из buildingsList
      const coordinates = [];
      this.buildingsList.forEach((item) => {
        if (item.data && Array.isArray(item.data)) {
          item.data.forEach((building) => {
            if (
              building.geolocation &&
              Array.isArray(building.geolocation) &&
              building.geolocation.length >= 2
            ) {
              coordinates.push(building.geolocation);
            }
          });
        }
      });

      if (coordinates.length === 0) {
        console.log("No coordinates found for buildings");
        return;
      }

      console.log("Found coordinates for buildings:", coordinates.length);

      // Находим границы всех зданий
      const lats = coordinates.map((coord) => coord[0]);
      const lngs = coordinates.map((coord) => coord[1]);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      // Добавляем отступы (padding)
      const latPadding = (maxLat - minLat) * 0.1 || 0.01;
      const lngPadding = (maxLng - minLng) * 0.1 || 0.01;

      const bounds = [
        [minLat - latPadding, minLng - lngPadding],
        [maxLat + latPadding, maxLng + lngPadding],
      ];

      try {
        // Зумируем карту к границам
        mapInstance.setBounds(bounds, {
          checkZoomRange: true,
          zoomMargin: 50,
        });

        // Включаем показ зданий
        this.showBuildings = true;
      } catch (error) {
        console.error("Error setting bounds:", error);
        // Fallback - просто центрируем на среднюю точку
        const centerLat = (minLat + maxLat) / 2;
        const centerLng = (minLng + maxLng) / 2;
        this.coords = [centerLat, centerLng];
        this.showBuildings = true;
      }
    },
    getRegionIcon(region) {
      // const regionColor = region.color || '#2C4084';
      const regionColor = "#2C4084";
      console.log("regionColor", regionColor);
      return {
        layout: "default#image",
        imageHref:
          "data:image/svg+xml;charset=utf-8," +
          encodeURIComponent(`
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Outer circle with shadow -->
            <circle cx="18" cy="18" r="16" fill="${regionColor}" stroke="white" stroke-width="2" opacity="0.9"/>

            <path d="M8 12 L10 8 L14 10 L20 8 L24 10 L28 12 L26 16 L28 20 L24 24 L20 26 L16 24 L12 26 L8 22 L10 18 L8 14 Z"
              fill="white"
              opacity="0.9"
              stroke="${regionColor}"
              stroke-width="1"/>
          </svg>
        `),
        imageSize: [36, 36],
        imageOffset: [-18, -18],
      };
    },
    getBuildingIcon(building) {
      const conclusionColor = this.getConclusionColor(building.finalconclusion);
      return {
        layout: "default#image",
        imageHref:
          "data:image/svg+xml;charset=utf-8," +
          encodeURIComponent(`
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="2" width="12" height="16" rx="1" fill="${conclusionColor}" stroke="white" stroke-width="1"/>
            <rect x="6" y="4" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="9" y="4" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="12" y="4" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="6" y="7" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="9" y="7" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="12" y="7" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="6" y="10" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="9" y="10" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="12" y="10" width="2" height="2" fill="white" opacity="0.8"/>
            <rect x="8" y="14" width="4" height="4" fill="white" opacity="0.9"/>
          </svg>
        `),
        imageSize: [28, 28],
        imageOffset: [-14, -14],
      };
    },
    getBuildingHintContent(building) {
      const parts = [];
      // Multilanguage support for building name
      if (building[`name_${this.currentLang}`]) {
        parts.push(building[`name_${this.currentLang}`]);
      } else if (building.name_ru || building.name_uz) {
        parts.push(building.name_ru || building.name_uz);
      } else {
        parts.push(this.$t("building") + ` #${building.id}`);
      }

      if (building.cadastral_number) {
        parts.push(
          this.$t("cadaster_number") + `: ${building.cadastral_number}`,
        );
      }

      if (building.year_construction) {
        parts.push(
          this.$t("year_construction") + `: ${building.year_construction}`,
        );
      }

      return parts.join(" | ");
    },
    clusterClicked(e) {
      console.log("Cluster clicked:", e);
    },
    buildingClicked(building) {
      console.log(
        "Building clicked:",
        building.name_ru || building.name_uz,
        building,
      );
      // Popup marker click orqali Leaflet tomonidan ochiladi
    },
    getSimpleBalloonContent(building) {
      const conclusionColor = this.getConclusionColor(building.finalconclusion);
      const conclusionBg = this.getConclusionBgColor(building.finalconclusion);

      // Находим регион для этого здания
      const currentRegion = this.regionsList.find(
        (r) => r.id === this.selectedRegionId,
      );
      const regionName = currentRegion
        ? currentRegion[`name_${this.currentLang}`] ||
          currentRegion.name_ru ||
          currentRegion.name_uz
        : "";

      return `
        <div style="
          padding: 0;
          font-family: 'Segoe UI', Arial, sans-serif;
          max-width: 280px;
          overflow: hidden;
          background: #fff;
        ">
          <!-- Header -->
          <div style="
            background: #2C4084;
            color: white;
            padding: 12px 16px;
          ">
            <div style="
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 2px;
            ">
              ${building[`name_${this.currentLang}`] ||
                building.name_ru ||
                building.name_uz ||
                this.$t("building") + ` #${building.id}`}
            </div>
            <div style="
              font-size: 13px;
              opacity: 0.85;
            ">
              ID: ${building.id}
            </div>
          </div>

          <!-- Content -->
          <div style="padding: 7px 8px;">
            ${
              regionName
                ? `
            <div style="margin-bottom: 8px;">
              <div style="color: #666; font-size: 13px; margin-bottom: 2px;">${this.$t(
                "region",
              )}</div>
              <div style="color: #333; font-size: 14px; font-weight: 500;">
                ${regionName}
              </div>
            </div>
            `
                : ""
            }

            ${
              building.district?.[`name_${this.currentLang}`] ||
              building.district?.name_ru ||
              building.district?.name_uz
                ? `
            <div style="margin-bottom: 8px;">
              <div style="color: #666; font-size: 13px; margin-bottom: 2px;">${this.$t(
                "district_or_city",
              )}</div>
              <div style="color: #333; font-size: 14px; font-weight: 500;">
                ${building.district?.[`name_${this.currentLang}`] ||
                  building.district?.name_ru ||
                  building.district?.name_uz ||
                  this.$t("not_specified")}
              </div>
            </div>
            `
                : ""
            }

            <div style="margin-bottom: 8px;">
              <div style="color: #666; font-size: 13px; margin-bottom: 2px;">${this.$t(
                "cadaster_number",
              )}</div>
              <div style="
                color: #333;
                font-size: 14px;
                font-weight: 500;
                word-break: break-all;
              ">
                ${building.cadastral_number || this.$t("not_specified")}
              </div>
            </div>

            <div style="margin-bottom: 8px;">
              <div style="color: #666; font-size: 13px; margin-bottom: 2px;">${this.$t(
                "year_construction",
              )}</div>
              <div style="color: #333; font-size: 14px; font-weight: 500;">
                ${building.year_construction || this.$t("not_specified")}
              </div>
            </div>

            <div style="margin-bottom: 12px;">
              <div style="color: #666; font-size: 13px; margin-bottom: 4px;">${this.$t(
                "finalconclusion",
              )}</div>
              <span style="
                color: ${conclusionColor};
                background: ${conclusionBg};
                padding: 3px 8px;
                border-radius: 4px;
                font-size: 13px;
                font-weight: 600;
                display: inline-block;
              ">
                ${this.getConclusionText(building.finalconclusion)}
              </span>
            </div>

            <div style="
              padding-top: 8px;
              border-top: 1px solid #e1e5e9;
              font-size: 12px;
              color: #999;
              text-align: center;
            ">
              ${building.geolocation[0].toFixed(
                4,
              )}, ${building.geolocation[1].toFixed(4)}
            </div>
          </div>
        </div>
      `;
    },
  },
};
</script>

<style>
/* Fullscreen styles for map */
.ymap-container {
  position: relative;
  transition: all 0.3s;
}
.ymap-container.is-loading-general-info .leaflet-map {
  cursor: wait !important;
}
.ymap-container.is-loading-general-info .leaflet-container,
.ymap-container.is-loading-general-info .leaflet-container * {
  cursor: wait !important;
}
.ymap-toolbar {
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  z-index: 2000;
  pointer-events: none;
}
.ymap-toolbar > * {
  pointer-events: all;
}
.ymap-search-wrapper {
  min-width: 280px;
  max-width: 520px;
}
.ymap-fullscreen-btn {
  background: #2c4084;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(44, 64, 132, 0.18);
  transition: transform 0.2s ease, background 0.2s ease;
  white-space: nowrap;
}
.ymap-fullscreen-btn:hover {
  transform: translateY(-1px);
  background: #1e346d;
}
.ymap-search-wrapper {
  flex: 1;
}
.ymap-search-input {
  width: 100%;
  max-width: 520px;
  padding: 12px 16px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  font-size: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
  outline: none;
}
.ymap-search-input::placeholder {
  color: #64748b;
}
.ymap-search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.12);
}
.ymap-container .leaflet-container {
  z-index: 1;
}
.ymap-container.fullscreen {
  position: fixed !important;
  left: 0;
  top: 0;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999;
  background: #fff;
  padding: 0 !important;
}
.ymap-container.fullscreen .leaflet-map {
  width: 100vw !important;
  height: 100vh !important;
  min-height: 100vh !important;
  max-height: 100vh !important;
}
/* Переопределение стилей Yandex Maps balloon для удаления отступов */
[class*="balloon__content"] {
  margin: 0 !important;
  padding: 0 !important;
}

[class*="balloon__body"] {
  margin: 0 !important;
  padding: 0 !important;
}

/* Дополнительно для других возможных классов */
[class*="ymaps"][class*="balloon"] [class*="content"] {
  margin: 0 !important;
  padding: 0 !important;
}

[class*="ymaps"][class*="balloon"] [class*="close-button"] {
  background-color: #fff !important;
  opacity: 1 !important;
}

.ymap-conclusion-badge {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  white-space: normal;
  line-height: 1.25;
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
}

.ymap-conclusion-badge--success {
  color: #155724;
  background: #d4edda;
  border-color: #b7e1c1;
}

.ymap-conclusion-badge--warning {
  color: #8a6d1d;
  background: #fff3cd;
  border-color: #ffe8a3;
}

.ymap-conclusion-badge--danger {
  color: #721c24;
  background: #f8d7da;
  border-color: #f1b0b7;
}

.ymap-conclusion-badge--info {
  color: #1f4d6b;
  background: #e7f3fb;
  border-color: #c9e6f7;
}
</style>
